// API Configurations
import CONFIG from './config/config.js';
import authService from './services/auth.service.js';
import csrfService from './services/csrf.service.js';

class ApiService {
    constructor(baseUrl) {
        this.baseUrl = baseUrl;
        this.apiPath = CONFIG.api.apiPath;
    }

    getApiUrl(endpoint) {
        return `${this.baseUrl}${this.apiPath}${endpoint}`;
    }

    getHeaders() {
        // SECURITY: Tokens are in httpOnly cookies (not accessible to JavaScript)
        // API requests should use credentials: 'include' to send cookies automatically
        // Backend reads token from cookie - no Authorization header needed
        return {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
            // No Authorization header - backend reads token from httpOnly cookie
        };
    }

    getImageUrl(imagePath) {
        // Make sure baseUrl is defined
        const baseUrl = this.baseUrl || '';
        
        // If no URL provided, return the fallback image
        if (!imagePath) {
            // Try to load the configured no-image path first
            const noImageUrl = `${baseUrl}${CONFIG.images.noImage.path}`;
            
            // Create an image object to test if the no-image file exists
            const img = new Image();
            img.onerror = () => {
                // If the no-image file fails to load, use the base64 fallback
                img.src = CONFIG.images.noImage.fallback;
            };
            img.src = noImageUrl;
            
            return noImageUrl;
        }
        
        // If it's already a full URL, use it as is
        if (imagePath.startsWith('http')) {
            return imagePath;
        }
        
        // If it's a static image from our site
        if (imagePath.startsWith('/images/')) {
            return `${baseUrl}${imagePath}`;
        }
        
        // If it's an uploaded image, ensure proper path
        if (!imagePath.startsWith('/uploads/')) {
            return `${baseUrl}/uploads/${imagePath.replace(/^\//, '')}`;
        }
        
        // If it's already a proper uploads path
        return `${baseUrl}${imagePath}`;
    }

    async getAllItems() {
        try {
            const url = this.getApiUrl('/menu?isActive=all');
            const headers = this.getHeaders();

            const response = await fetch(url, { 
                headers,
                credentials: 'include'
            });
            
            if (!response.ok) {
                if (response.status === 401) {
                    window.location.href = '/login.html';
                    throw new Error('Authentication required');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching menu items:', error);
            throw error;
        }
    }

    async getItemById(id) {
        try {
            const url = this.getApiUrl(`/menu/${id}`);
            const response = await fetch(url, {
                headers: this.getHeaders(),
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching menu item:', error);
            throw error;
        }
    }

    /**
     * Fetch all menu categories
     */
    async getCategories() {
        try {
            const response = await fetch(this.getApiUrl('/menu/categories'), {
                headers: this.getHeaders(),
                credentials: 'include'
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Error fetching categories:', error);
            throw error;
        }
    }

    /**
     * Create a new menu item
     * @param {FormData} formData - The form data containing the menu item details and images
     * @returns {Promise<Object>} Response from the server
     */
    async createMenuItem(formData) {
        try {
            // Get fresh CSRF token before create request
            const csrfToken = await csrfService.getToken();
            // Don't add _csrf to FormData - backend validation rejects it
            // CSRF token should only be in headers
            
            // Get authentication headers from auth service
            const authHeaders = await authService.getAuthHeaders();
            
            const response = await fetch(this.getApiUrl('/menu'), {
                method: 'POST',
                headers: {
                    ...authHeaders,
                    'X-CSRF-Token': csrfToken, // CSRF token only in headers
                    // Don't set Content-Type for FormData
                },
                credentials: 'include',
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
                console.error('Error response from server:', errorData);
                throw new Error(errorData.message || errorData.error?.message || 'Failed to create menu item');
            }

            return await response.json();
        } catch (error) {
            console.error('Error creating menu item:', error);
            throw error;
        }
    }

    async updateItem(id, formData) {
        try {
            // Get fresh CSRF token before update request
            const csrfToken = await csrfService.getToken();
            
            // Get authentication headers from auth service
            const authHeaders = await authService.getAuthHeaders();
            
            const response = await fetch(this.getApiUrl(`/menu/${id}`), {
                method: 'PUT',
                headers: {
                    ...authHeaders,
                    'X-CSRF-Token': csrfToken, // Add CSRF token to headers
                    // Don't set Content-Type for FormData
                },
                credentials: 'include',
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Error response from server:', errorData);
                throw new Error(errorData.message || 'Failed to update menu item');
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating menu item:', error);
            throw error;
        }
    }

    async deleteItem(id) {
        try {
            // Get fresh CSRF token before delete request
            const csrfToken = await csrfService.getToken();
            
            const headers = {
                ...this.getHeaders(),
                'X-CSRF-Token': csrfToken
            };
            
            const response = await fetch(this.getApiUrl(`/menu/${id}`), {
                method: 'DELETE',
                headers: headers,
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
                throw new Error(errorData.message || errorData.error?.message || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error deleting menu item:', error);
            throw error;
        }
    }

    /**
     * Add a new category
     * @param {Object} categoryData - Object containing category information
     * @param {string} categoryData.name - The name of the category to add
     * @returns {Promise<Object>} Response from the server
     */
    async addCategory(categoryData) {
        try {
            // Get CSRF token for state-changing operation
            const csrfToken = await csrfService.getToken();
            
            const headers = {
                ...this.getHeaders(),
                'X-CSRF-Token': csrfToken
            };
            
            const response = await fetch(this.getApiUrl('/menu/categories'), {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(categoryData),
                credentials: 'include'
            });
            
            // Try to get JSON responses
            const responseData = await response.json().catch(() => {
                return { 
                    success: false, 
                    error: { message: `HTTP error! status: ${response.status}` } 
                };
            });
            
            // Check for specific error codes
            if (response.status === 401 || response.status === 403) {
                // Authentication/authorization error
                throw new Error(`Authentication error: ${responseData.message || 'Not authorized'}`);
            } else if (response.status === 400) {
                // Validation error
                throw new Error(`Validation error: ${responseData.message || 'Invalid data'}`);
            } else if (response.status === 409) {
                // Conflict (duplicate)
                throw new Error(`Category already exists: ${categoryData.name}`);
            } else if (!response.ok) {
                // Other error
                throw new Error(responseData.message || `Error: ${response.status}`);
            }
            
            return responseData;
        } catch (error) {
            console.error('Error adding category:', error);
            return {
                success: false,
                error: {
                    message: error.message || 'Failed to add category. Please try again.'
                }
            };
        }
    }

    /**
     * Delete a category
     * @param {string} categoryName - The name of the category to delete
     * @returns {Promise<Object>} Response from the server
     */
    async deleteCategory(categoryName) {
        try {
            // Get CSRF token for state-changing operation
            const csrfToken = await csrfService.getToken();
            
            const headers = {
                ...this.getHeaders(),
                'X-CSRF-Token': csrfToken
            };
            
            const response = await fetch(this.getApiUrl(`/menu/categories/${encodeURIComponent(categoryName)}`), {
                method: 'DELETE',
                headers: headers,
                credentials: 'include'
            });
            
            const responseData = await response.json().catch(() => {
                return { 
                    success: false, 
                    error: { message: `HTTP error! status: ${response.status}` } 
                };
            });
            
            if (response.status === 401 || response.status === 403) {
                throw new Error(`Authentication error: ${responseData.message || 'Not authorized'}`);
            } else if (!response.ok) {
                throw new Error(responseData.message || `Error: ${response.status}`);
            }
            
            return responseData;
        } catch (error) {
            console.error('Error deleting category:', error);
            return {
                success: false,
                error: {
                    message: error.message || 'Failed to delete category. Please try again.'
                }
            };
        }
    }

    async updateMenuItem(itemId, formData) {
        try {
            // Get fresh CSRF token before each request
            const csrfToken = await csrfService.getToken();
            
            // Add CSRF token to form data
            formData.append('_csrf', csrfToken);
            
            // Get authentication headers from auth service
            const authHeaders = await authService.getAuthHeaders();
            
            // Use the correct API URL with /api prefix
            const response = await fetch(this.getApiUrl(`/menu/${itemId}`), {
                method: 'PUT',
                headers: {
                    ...authHeaders,
                    'X-CSRF-Token': csrfToken, // Add CSRF token to headers as well
                    // Don't set Content-Type for FormData - the browser will set it automatically
                    // with the correct boundary
                },
                credentials: 'include',
                body: formData
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
                console.error('Error response from server:', errorData);
                throw new Error(errorData.message || errorData.error?.message || 'Failed to update menu item');
            }
            
            const data = await response.json();
            return data;
            
        } catch (error) {
            console.error('Error updating menu item:', error);
            throw error;
        }
    }
}

const apiService = new ApiService(CONFIG.api.baseUrl);

export default apiService; 